/**
 * Dashboard Component
 * Broker-specific dashboard showing shipment statistics and list
 * with filtering, sorting, and search capabilities
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  HStack,
  VStack,
  StackDivider,
  useToast,
  Spinner,
  Text,
  IconButton,
  Flex,
  Image,
  Avatar,
  Icon,
} from '@chakra-ui/react';
import {
  FaSearch,
  FaSync,
  FaChevronDown,
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getDashboardData } from '../services/shipment.service';
import { DashboardData, PaginationParams, ShipmentStatusBucket } from '../types/shipment.types';
import ShipmentTable from './ShipmentTable';
import UserProfileDrawer from './UserProfileDrawer';

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  
  // Pagination and filtering state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [activeBucket, setActiveBucket] = useState<ShipmentStatusBucket | null>(null);
  const [sortBy, setSortBy] = useState<'etd' | 'brl_reference' | 'shipment_status' | 'created_at'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  /**
   * Load dashboard data
   */
  const loadDashboardData = useCallback(async () => {
    try {
      const data = await getDashboardData();
      setDashboardData(data);
    } catch (error: any) {
      console.error('[Dashboard] Error loading dashboard:', error.message);
      toast({
        title: t('common.error'),
        description: error.message || t('common.error'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [toast, user]);

  /**
   * Initial load
   */
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadDashboardData();
      setLoading(false);
    };
    init();
  }, [loadDashboardData]);

  /**
   * Refresh dashboard
   */
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
    toast({
      title: t('dashboard.refresh_success'),
      description: t('dashboard.refresh_desc'),
      status: 'success',
      duration: 2000,
    });
  };

  /**
   * Handle logout
   */
  const handleLogout = async () => {
    try {
      await logout();
      toast({
        title: t('dashboard.logout_success'),
        description: t('dashboard.logout_desc'),
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  /**
   * Handle search with debounce
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1); // Reset to first page on search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  /**
   * Toggle the stat rail's active bucket filter — clicking the active one clears it
   */
  const toggleBucket = (bucket: ShipmentStatusBucket) => {
    setActiveBucket(prev => (prev === bucket ? null : bucket));
    setPage(1); // Reset to first page on filter change
  };

  /**
   * Get pagination params
   */
  const getPaginationParams = (): PaginationParams => ({
    page,
    limit,
    sort_by: sortBy,
    sort_order: sortOrder,
    ...(search && { search }),
    ...(activeBucket && { status_bucket: activeBucket }),
  });

  if (loading) {
    return (
      <Flex minH="100vh" align="center" justify="center">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text>{t('dashboard.loading')}</Text>
        </VStack>
      </Flex>
    );
  }

  if (!dashboardData) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text>{t('dashboard.no_data')}</Text>
      </Container>
    );
  }

  const stats = dashboardData.stats;

  return (
    <Container maxW="container.xl" py={10}>
      {/* Header */}
      <Flex
        justify="space-between"
        align="center"
        pb={6}
        mb={10}
        borderBottom="1px solid"
        borderColor="appleCardBorder"
      >
        <HStack spacing={3}>
          <Image src="/BRL Logo Blue.png" alt="BRL Logo" height="32px" objectFit="contain" />
          <Text color="appleTextSecondary" fontSize="sm" fontWeight="medium">
            {dashboardData.broker_name}
          </Text>
        </HStack>

        <HStack spacing={4}>
          <InputGroup size="sm" maxW="220px" display={{ base: 'none', md: 'flex' }}>
            <InputLeftElement pointerEvents="none">
              <Icon as={FaSearch} color="appleTextSecondary" boxSize={3.5} />
            </InputLeftElement>
            <Input
              variant="flushed"
              borderColor="appleCardBorder"
              placeholder={t('dashboard.search_placeholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </InputGroup>

          <IconButton
            aria-label="Refresh dashboard"
            icon={refreshing ? <Spinner size="sm" /> : <FaSync />}
            onClick={handleRefresh}
            isDisabled={refreshing}
            variant="ghost"
            size="sm"
            color="appleTextSecondary"
          />

          <Button
            variant="ghost"
            size="sm"
            px={1}
            onClick={() => setIsProfileOpen(true)}
          >
            <HStack spacing={2}>
              <Avatar size="sm" name={user?.email || 'User'} bg="appleAccent" />
              <Box as={FaChevronDown} fontSize="xs" color="appleTextSecondary" display={{ base: 'none', md: 'block' }} />
            </HStack>
          </Button>
        </HStack>
      </Flex>

      {/* Hero stat */}
      <Box mb={10}>
        <Heading fontSize={{ base: '40px', md: '56px' }} fontWeight="700" lineHeight="1.07" color="appleTextPrimary">
          {stats.total_shipments}
        </Heading>
        <Text color="appleTextSecondary" fontSize="17px" mt={1}>
          {t('dashboard.total_shipments_help')}
        </Text>
      </Box>

      {/* Stat rail — click a bucket to filter the table below */}
      <HStack
        spacing={{ base: 6, md: 10 }}
        divider={<StackDivider borderColor="appleCardBorder" />}
        mb={10}
        overflowX="auto"
      >
        {([
          ['draft', t('dashboard.draft'), stats.draft_count],
          ['in_progress', t('dashboard.in_progress'), stats.in_progress_count],
          ['submitted', t('dashboard.submitted'), stats.submitted_count],
          ['concluded', t('dashboard.completed'), stats.completed_count],
        ] as [ShipmentStatusBucket, string, number][]).map(([bucket, label, count]) => {
          const isActive = activeBucket === bucket;
          return (
            <VStack
              key={bucket}
              align="start"
              spacing={0}
              minW="fit-content"
              cursor="pointer"
              onClick={() => toggleBucket(bucket)}
              opacity={activeBucket && !isActive ? 0.5 : 1}
              transition="opacity 0.15s ease"
            >
              <Text fontSize="xs" color={isActive ? 'appleAccent' : 'appleTextSecondary'} fontWeight={isActive ? '600' : 'normal'}>
                {label}
              </Text>
              <Text fontSize="xl" fontWeight="600" color={isActive ? 'appleAccent' : 'appleTextPrimary'}>
                {count}
              </Text>
            </VStack>
          );
        })}
      </HStack>

      {/* Shipment Table */}
      <ShipmentTable
        paginationParams={getPaginationParams()}
        onPageChange={setPage}
      />

      {/* User Profile Drawer */}
      <UserProfileDrawer
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
      />
    </Container>
  );
};

export default Dashboard;
